import React, { createContext, useContext, useState, useEffect } from 'react'
import { signInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendEmailVerification,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { auth, db } from '../utils/firebase'

const AuthContext = createContext(null)

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({
  prompt: 'select_account'
})

// Initialize Apple Auth Provider
const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tokenAuthInProgress, setTokenAuthInProgress] = useState(false)
  const [error, setError] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [socialSignUpInProgress, setSocialSignUpInProgress] = useState(false)
  
  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Pull the latest Firebase Auth state from the server so that any
        // email change completed via the action handler (which updates the
        // server-side email but leaves the local cache stale) is reflected
        // before we load the Firestore profile.
        try {
          await user.reload()
        } catch (err) {
          console.error('Error reloading Firebase Auth user:', err)
        }

        setCurrentUser(user)
        await loadUserProfile(user.uid)

        // Sync emailVerified status to Firestore for directory access control
        // This ensures the directory can check verification status
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid))
          if (userDoc.exists()) {
            const storedEmailVerified = userDoc.data().emailVerified
            // Only update if the status has changed (to avoid unnecessary writes)
            if (storedEmailVerified !== user.emailVerified) {
              await updateDoc(doc(db, 'users', user.uid), {
                emailVerified: user.emailVerified,
                updatedAt: serverTimestamp()
              })
            }
          }
        } catch (err) {
          console.error('Error syncing emailVerified status:', err)
        }
      } else {
        setCurrentUser(null)
        setUserProfile(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Check for auth token on mount
  console.log("URL on mount:", window.location.href)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("authToken")) {
      setTokenAuthInProgress(true)
    }
  }, [])

  // Handle auto-login from Android app via custom token
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const authToken = urlParams.get("authToken")
    
    if (authToken && !currentUser) {
      signInWithCustomToken(auth, authToken)
        .then(() => {
          // Remove token from URL for security
          urlParams.delete("authToken")
          const newUrl = window.location.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "")
          window.history.replaceState({}, "", newUrl)
          setTokenAuthInProgress(false)
        })
        .catch((err) => {
          console.error("Auto-login failed:", err)
          setTokenAuthInProgress(false)
        })
    }
  }, [currentUser])

  // Load subscription when userProfile changes
  useEffect(() => {
    const loadSubscription = async () => {
      if (!userProfile?.neighborhoodId) {
        setSubscription(null)
        setSubscriptionLoading(false)
        return
      }
      
      try {
        const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
        if (neighborhoodDoc.exists()) {
          const data = neighborhoodDoc.data()
          setSubscription(data.subscription || null)
        } else {
          setSubscription(null)
        }
      } catch (error) {
        console.error('Error loading subscription:', error)
        setSubscription(null)
      } finally {
        setSubscriptionLoading(false)
      }
    }
    
    loadSubscription()
  }, [userProfile?.neighborhoodId])

  // Load user profile from Firestore
  async function loadUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        const data = userDoc.data()

        // "Last login" shown in Settings is the value that was already in
        // Firestore BEFORE this browser session touched it — so a user who
        // signed in yesterday and refreshes today still sees yesterday.
        // We stamp a new serverTimestamp exactly once per sessionStorage
        // lifetime (a tab close/reopen counts as a new login); a plain
        // refresh keeps the sentinel set so we don't shift the window.
        const previousLastLoginAt = data.lastLoginAt?.toDate?.() || null
        if (!sessionStorage.getItem('nt.lastLoginWritten')) {
          sessionStorage.setItem('nt.lastLoginWritten', '1')
          updateDoc(doc(db, 'users', uid), { lastLoginAt: serverTimestamp() })
            .catch((err) => console.warn('Failed to stamp lastLoginAt:', err))
        }

        // If Firebase Auth's email differs from Firestore (because the user
        // verified an email change via the action handler), sync Firestore so
        // the profile UI shows the new address and clear any pendingEmail
        // stamp from the old verifyBeforeUpdateEmail flow.
        const firebaseEmail = auth.currentUser?.email
        let resolvedEmail = data.email || ''
        let resolvedPendingEmail = data.pendingEmail || null
        if (firebaseEmail && firebaseEmail !== data.email) {
          try {
            await updateDoc(doc(db, 'users', uid), {
              email: firebaseEmail,
              pendingEmail: null,
              pendingEmailSentAt: null,
              updatedAt: serverTimestamp()
            })
            resolvedEmail = firebaseEmail
            resolvedPendingEmail = null
          } catch (e) {
            console.error('Failed to sync email after change:', e)
          }
        }

        // Resolve a single canonical first/last name from three possibly-
        // disagreeing Firestore fields (`name`, `firstName`, `lastName`).
        //
        // Why: legacy docs only had `name`. After the first/last split, new
        // saves write all three. But some accounts have drift — e.g. `name`
        // was edited by one path while `firstName` was left at an older
        // value. We've actually seen this in production (a user's header
        // greeting kept showing an old first name even after they updated
        // their full name).
        //
        // Rule: if storedFirst + storedLast (joined with a space) matches
        // the canonical `name`, trust the stored fields. Otherwise, `name`
        // wins as the source of truth and we split it into first/last so
        // every part of the UI stays consistent.
        const canonicalName = (data.name || '').trim()
        const storedFirstName = (data.firstName || '').trim()
        const storedLastName = (data.lastName || '').trim()
        const composedFromStored = [storedFirstName, storedLastName]
          .filter(Boolean).join(' ').trim()

        const splitName = canonicalName.split(/\s+/).filter(Boolean)
        const splitFirstName = splitName[0] || ''
        const splitLastName = splitName.slice(1).join(' ')

        let derivedFirstName
        let derivedLastName
        if (canonicalName && composedFromStored && composedFromStored !== canonicalName) {
          // Drift detected — prefer the split of the canonical `name` so
          // the header, dropdown, and edit form all show the same person.
          derivedFirstName = splitFirstName
          derivedLastName = splitLastName
        } else {
          // Either no `name` set yet, or stored fields agree with `name`.
          // Use stored fields when present, fall back to the split for
          // legacy docs that only have `name`.
          derivedFirstName = storedFirstName || splitFirstName
          derivedLastName = storedLastName || splitLastName
        }

        setUserProfile({
          uid,
          email: resolvedEmail,
          name: canonicalName || [derivedFirstName, derivedLastName].filter(Boolean).join(' '),
          firstName: derivedFirstName,
          lastName: derivedLastName,
          phone: data.phone || null,
          address: data.address || '',
          streetAddress: extractStreetAddress(data.address || ''),
          neighborhoodId: data.neighborhoodId || '',
          isVerified: data.isVerified || false,
          isAdmin: data.isAdmin || false,
          isBoardMember: data.isBoardMember || false,
          isSuperAdmin: data.isSuperAdmin || false,
          joinedDate: data.joinedDate?.toDate() || new Date(),
          showPhone: data.showPhone ?? true,
          showEmail: data.showEmail ?? true,
          showInDirectory: data.showInDirectory ?? true,
          emailVerified: data.emailVerified || false,
          profilePhotoURL: data.profilePhotoURL || null,
          occupation: data.occupation || null,
          // Optional bio fields surfaced by the Directory hover card.
          // All three are user-facing free-text and stored as-typed.
          partner: data.partner || null,
          children: data.children || null,
          hobbies: data.hobbies || null,
          hiddenNeighbors: Array.isArray(data.hiddenNeighbors) ? data.hiddenNeighbors : [],
          pendingEmail: resolvedPendingEmail,
          lastLoginAt: previousLastLoginAt
        })
      }
    } catch (err) {
      console.error('Error loading user profile:', err)
      setError(err.message)
    }
  }

  // Extract street address from full address
  function extractStreetAddress(fullAddress) {
    const parts = fullAddress.split(',')
    return parts[0]?.trim() || fullAddress
  }

  // Sign in with email and password
  async function signIn(email, password) {
    try {
      setError(null)
      const result = await signInWithEmailAndPassword(auth, email, password)
      return result.user
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Sign in with Google (for returning users)
  async function signInWithGoogle() {
    try {
      setError(null)
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (!userDoc.exists()) {
        // User doesn't have a profile - they need to register first
        await firebaseSignOut(auth)
        throw new Error('NO_PROFILE')
      }

      return user
    } catch (err) {
      if (err.message === 'NO_PROFILE') {
        setError('No account found. Please register with an invitation code first.')
      } else {
        setError(err.message)
      }
      throw err
    }
  }

  // Sign in with Apple (for returning users)
  async function signInWithApple() {
    try {
      setError(null)
      const result = await signInWithPopup(auth, appleProvider)
      const user = result.user

      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (!userDoc.exists()) {
        // User doesn't have a profile - they need to register first
        await firebaseSignOut(auth)
        throw new Error('NO_PROFILE')
      }

      return user
    } catch (err) {
      if (err.message === 'NO_PROFILE') {
        setError('No account found. Please register with an invitation code first.')
      } else {
        setError(err.message)
      }
      throw err
    }
  }

  // Sign up with invitation code
  async function signUp(email, password, invitationCode, userData) {
    try {
      setError(null)
      
      // Create auth user
      const result = await createUserWithEmailAndPassword(auth, email, password)
      const user = result.user
      
      // Create user profile in Firestore. Persist firstName/lastName
      // alongside the joined `name` when the caller supplies them so the
      // schema matches Android EditProfile + Web AdminRegistration. Falls
      // back to splitting `name` for callers that don't pass split fields.
      const splitFromName = (userData.name || '').trim().split(/\s+/)
      const firstName = userData.firstName || splitFromName[0] || ''
      const lastName = userData.lastName || splitFromName.slice(1).join(' ') || ''
      await setDoc(doc(db, 'users', user.uid), {
        email: email.toLowerCase(),
        name: userData.name,
        firstName,
        lastName,
        phone: userData.phone || null,
        address: userData.address,
        neighborhoodId: userData.neighborhoodId,
        invitationCode: invitationCode,
        isVerified: false,
        isAdmin: false,
        isBoardMember: false,
        isSuperAdmin: false,
        showPhone: true,
        showEmail: true,
        showInDirectory: true,
        hiddenNeighbors: [],
        joinedDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      
      // Send email verification
      await sendEmailVerification(user)

      return user
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Sign up with Google (after invitation code validation)
  async function signUpWithGoogle(invitationCode, invitationData) {
    try {
      setError(null)
      setSocialSignUpInProgress(true)
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Check if user already has a profile (by UID)
      const existingUserDoc = await getDoc(doc(db, 'users', user.uid))
      if (existingUserDoc.exists()) {
        // User already registered - sign out and throw error
        await firebaseSignOut(auth)
        throw new Error('An account with this email already exists.')
      }

      // Also check if email exists in any user document (in case of linked accounts)
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '==', user.email.toLowerCase())
      )
      const emailSnapshot = await getDocs(emailQuery)
      if (!emailSnapshot.empty) {
        // Email already registered - sign out and throw error
        await firebaseSignOut(auth)
        throw new Error('An account with this email already exists.')
      }

      // Extract name from Google profile
      const displayName = user.displayName || ''
      const nameParts = displayName.split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Create user profile in Firestore. Persist firstName/lastName
      // alongside `name` so the schema matches our other signup flows.
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email.toLowerCase(),
        name: displayName || user.email.split('@')[0],
        firstName,
        lastName,
        phone: user.phoneNumber || null,
        address: invitationData.address,
        neighborhoodId: invitationData.neighborhoodId,
        invitationCode: invitationCode,
        isVerified: false,
        isAdmin: false,
        isBoardMember: false,
        isSuperAdmin: false,
        showPhone: true,
        showEmail: true,
        showInDirectory: true,
        hiddenNeighbors: [],
        emailVerified: user.emailVerified, // Google accounts are pre-verified
        profilePhotoURL: user.photoURL || null,
        joinedDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      setSocialSignUpInProgress(false)
      return { user, isExisting: false }
    } catch (err) {
      setSocialSignUpInProgress(false)
      setError(err.message)
      throw err
    }
  }

  // Sign up with Apple (after invitation code validation)
  async function signUpWithApple(invitationCode, invitationData) {
    try {
      setError(null)
      setSocialSignUpInProgress(true)
      const result = await signInWithPopup(auth, appleProvider)
      const user = result.user

      // Check if user already has a profile (by UID)
      const existingUserDoc = await getDoc(doc(db, 'users', user.uid))
      if (existingUserDoc.exists()) {
        // User already registered - sign out and throw error
        await firebaseSignOut(auth)
        throw new Error('An account with this email already exists.')
      }

      // Also check if email exists in any user document (in case of linked accounts)
      if (user.email) {
        const emailQuery = query(
          collection(db, 'users'),
          where('email', '==', user.email.toLowerCase())
        )
        const emailSnapshot = await getDocs(emailQuery)
        if (!emailSnapshot.empty) {
          // Email already registered - sign out and throw error
          await firebaseSignOut(auth)
          throw new Error('An account with this email already exists.')
        }
      }

      // Apple may provide name only on first sign-in, extract it from the credential
      // Get name from the Apple credential if available
      const credential = OAuthProvider.credentialFromResult(result)
      let displayName = user.displayName || ''
      let appleFirstName = ''
      let appleLastName = ''

      // If no display name from user object, try to get from the result
      if (!displayName && result._tokenResponse?.fullName) {
        const fullName = result._tokenResponse.fullName
        appleFirstName = fullName.givenName || ''
        appleLastName = fullName.familyName || ''
        displayName = [appleFirstName, appleLastName].filter(Boolean).join(' ')
      } else if (displayName) {
        const nameParts = displayName.split(' ')
        appleFirstName = nameParts[0] || ''
        appleLastName = nameParts.slice(1).join(' ') || ''
      }

      const fallbackName = displayName || (user.email ? user.email.split('@')[0] : 'Apple User')

      // Create user profile in Firestore — keep firstName/lastName so the
      // schema matches our other signup paths.
      await setDoc(doc(db, 'users', user.uid), {
        email: (user.email || '').toLowerCase(),
        name: fallbackName,
        firstName: appleFirstName || fallbackName.split(' ')[0] || '',
        lastName: appleLastName || fallbackName.split(' ').slice(1).join(' ') || '',
        phone: user.phoneNumber || null,
        address: invitationData.address,
        neighborhoodId: invitationData.neighborhoodId,
        invitationCode: invitationCode,
        isVerified: false,
        isAdmin: false,
        isBoardMember: false,
        isSuperAdmin: false,
        showPhone: true,
        showEmail: true,
        showInDirectory: true,
        hiddenNeighbors: [],
        emailVerified: true, // Apple accounts are verified
        profilePhotoURL: user.photoURL || null,
        joinedDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      setSocialSignUpInProgress(false)
      return { user, isExisting: false }
    } catch (err) {
      setSocialSignUpInProgress(false)
      setError(err.message)
      throw err
    }
  }

  // Sign out
  async function signOut() {
    try {
      await firebaseSignOut(auth)
      setCurrentUser(null)
      setUserProfile(null)
      // Clear the once-per-session guard so the next sign-in in this
      // tab actually stamps a fresh lastLoginAt.
      try { sessionStorage.removeItem('nt.lastLoginWritten') } catch (_) {}
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Send password reset email
  async function resetPassword(email) {
    try {
      setError(null)
      await sendPasswordResetEmail(auth, email)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Send verification email to current user
  async function sendVerificationEmail() {
    if (!currentUser) throw new Error("No user logged in")

    try {
      setError(null)
      await sendEmailVerification(currentUser)
      return true
    } catch (err) {
      let friendlyMessage = err.message
      if (err.code === 'auth/too-many-requests') {
        friendlyMessage = "Too many attempts. Please try again later."
      }
      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }

  // Change the user's password. Requires re-authentication with the
  // current password (Firebase enforces "recent login" for sensitive
  // operations). Used by the /secure-account page.
  async function changePassword(currentPassword, newPassword) {
    if (!currentUser) throw new Error("No user logged in")
    if (!newPassword || newPassword.length < 6) {
      throw new Error("New password must be at least 6 characters.")
    }
    try {
      setError(null)
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword)
      await reauthenticateWithCredential(currentUser, credential)
      await updatePassword(currentUser, newPassword)
      return true
    } catch (err) {
      let friendlyMessage = err.message
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        friendlyMessage = "Incorrect current password. Please try again."
      } else if (err.code === 'auth/weak-password') {
        friendlyMessage = "That password is too weak. Choose a stronger one (at least 6 characters)."
      } else if (err.code === 'auth/too-many-requests') {
        friendlyMessage = "Too many attempts. Please try again later."
      }
      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }

  // Cancel a pending email change initiated from this account. Calls our
  // Cloud Function to clear pendingEmail/pendingEmailSentAt on the user's
  // Firestore doc. Note: doesn't invalidate the underlying verifyAndChange
  // oobCode (Firebase doesn't expose that) — pair with changePassword for
  // full lockdown.
  async function cancelPendingEmailChange() {
    if (!currentUser) throw new Error("No user logged in")
    try {
      setError(null)
      const functions = getFunctions()
      const callable = httpsCallable(functions, 'cancelPendingEmailChange')
      await callable()
      // Refresh local profile so the pending banner clears immediately.
      await loadUserProfile(currentUser.uid)
      return true
    } catch (err) {
      const friendlyMessage = err?.message || "Could not cancel the email change."
      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }

  // Force every other signed-in device to re-authenticate by revoking all
  // refresh tokens server-side. The current session keeps working for up
  // to ~1 hour (until the next ID-token refresh).
  async function signOutAllDevices() {
    if (!currentUser) throw new Error("No user logged in")
    try {
      setError(null)
      const functions = getFunctions()
      const callable = httpsCallable(functions, 'signOutAllDevices')
      await callable()
      return true
    } catch (err) {
      const friendlyMessage = err?.message || "Could not sign out other devices."
      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }

  // Resend the styled verification email for an in-flight email *change*
  // (the user has a pendingEmail; the link must go to that pending address,
  // not the currently-signed-in one). Re-uses the same Cloud Function as
  // updateEmail() so the new inbox gets our branded template.
  async function resendEmailChangeVerification(pendingEmail) {
    if (!currentUser) throw new Error("No user logged in")
    if (!pendingEmail) throw new Error("No pending email change to resend.")
    try {
      setError(null)
      const functions = getFunctions()
      const sendEmailChange = httpsCallable(functions, 'sendEmailChangeVerification')
      await sendEmailChange({ newEmail: pendingEmail })
      return true
    } catch (err) {
      const code = err?.code || ''
      let friendlyMessage = err?.message || 'Could not resend the verification email.'
      if (code === 'functions/already-exists') {
        friendlyMessage = "This email address is already registered to another account."
      } else if (code === 'functions/invalid-argument') {
        friendlyMessage = err.message || "That email address looks invalid."
      } else if (code === 'functions/unauthenticated') {
        friendlyMessage = "Session expired. Please sign in again."
      }
      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }


  // Check if email is already in use
  async function checkEmailExists(email) {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase())
      )
      const snapshot = await getDocs(usersQuery)
      return !snapshot.empty
    } catch (err) {
      console.error('Error checking email:', err)
      return false
    }
  }

  // Update email with verification
  async function updateEmail(newEmail, password) {
    if (!currentUser) throw new Error("No user logged in")
    
    try {
      setError(null)
      
      // Check if email is already in use
      const emailExists = await checkEmailExists(newEmail)
      if (emailExists) {
        throw new Error("This email address is already in use by another account.")
      }
      
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(currentUser.email, password)
      await reauthenticateWithCredential(currentUser, credential)

      // Send the verification email via our Cloud Function so the user gets
      // the NeighborTopia-styled message (logo, button, app store badges)
      // instead of Firebase Auth's default plain-text email. Mirrors the
      // iOS / Android email-change flow.
      const functions = getFunctions()
      const sendEmailChange = httpsCallable(functions, 'sendEmailChangeVerification')
      try {
        await sendEmailChange({ newEmail })
      } catch (callableErr) {
        // Map Cloud Function HttpsError codes back to friendly errors so the
        // catch block below can surface them correctly.
        const code = callableErr?.code || ''
        if (code === 'functions/already-exists') {
          throw Object.assign(new Error('This email address is already registered to another account.'), { code: 'auth/email-already-in-use' })
        }
        if (code === 'functions/invalid-argument') {
          throw Object.assign(new Error(callableErr.message || 'That email address looks invalid.'), { code: 'auth/invalid-email' })
        }
        throw callableErr
      }

      // Store the pending email in Firestore
      await updateDoc(doc(db, "users", currentUser.uid), {
        pendingEmail: newEmail,
        pendingEmailSentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      return true
    } catch (err) {
      // Map Firebase error codes to friendly messages
      let friendlyMessage = err.message

      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        friendlyMessage = "Incorrect password. Please try again."
      } else if (err.code === 'auth/invalid-email') {
        friendlyMessage = "Please enter a valid email address."
      } else if (err.code === 'auth/email-already-in-use') {
        friendlyMessage = "This email address is already in use by another account."
      } else if (err.code === 'auth/requires-recent-login') {
        friendlyMessage = "Please sign out and sign back in, then try again."
      } else if (err.code === 'auth/too-many-requests') {
        friendlyMessage = "Too many attempts. Please try again later."
      }

      setError(friendlyMessage)
      throw new Error(friendlyMessage)
    }
  }

  // Update user profile
  async function updateProfile(updates) {
    if (!currentUser) return
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...updates,
        updatedAt: serverTimestamp()
      })
      await loadUserProfile(currentUser.uid)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Check if a neighbor is hidden
  function isNeighborHidden(neighborId) {
    const hidden = userProfile?.hiddenNeighbors
    return Array.isArray(hidden) ? hidden.includes(neighborId) : false
  }

  // Hide a neighbor
  async function hideNeighbor(neighborId) {
    if (!currentUser || !userProfile) return
    
    const updatedHidden = [...(userProfile.hiddenNeighbors || []), neighborId]
    await updateProfile({ hiddenNeighbors: updatedHidden })
  }

  // Unhide a neighbor
  async function unhideNeighbor(neighborId) {
    if (!currentUser || !userProfile) return

    const updatedHidden = (userProfile.hiddenNeighbors || []).filter(id => id !== neighborId)
    await updateProfile({ hiddenNeighbors: updatedHidden })
  }

  // Update user's neighborhood (for joining a new neighborhood)
  async function updateUserNeighborhood(neighborhoodId, address) {
    if (!currentUser) throw new Error("No user logged in")

    try {
      // Fetch the neighborhood name
      let neighborhoodName = neighborhoodId
      try {
        const neighborhoodDoc = await getDoc(doc(db, "neighborhoods", neighborhoodId))
        if (neighborhoodDoc.exists()) {
          neighborhoodName = neighborhoodDoc.data().name || neighborhoodId
        }
      } catch (e) {
        console.error("Error fetching neighborhood name:", e)
      }

      // Update user document
      await updateDoc(doc(db, 'users', currentUser.uid), {
        neighborhoodId: neighborhoodId,
        neighborhoodName: neighborhoodName,
        address: address,
        streetAddress: address.split(',')[0]?.trim() || address,
        updatedAt: serverTimestamp()
      })

      // Reload user profile
      await loadUserProfile(currentUser.uid)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Check if subscription is active
  const isSubscriptionActive = () => {
    console.log("Subscription check:", { subscription, subscriptionLoading, isSuperAdmin: userProfile?.isSuperAdmin })
    // Super admins bypass subscription check
    if (userProfile?.isSuperAdmin) return true

    // Users without a neighborhood don't need subscription check
    // (they'll see the NoNeighborhoodPage instead)
    if (!userProfile?.neighborhoodId || userProfile?.neighborhoodId === '') return true

    // Don't show paywall while subscription is still loading
    if (subscriptionLoading) return true

    if (!subscription) return false

    const status = subscription.status

    // Check if subscription is still within the paid period
    const endDate = subscription.currentPeriodEnd?.toDate?.() ||
                   subscription.trialEndsAt?.toDate?.() ||
                   (subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null) ||
                   (subscription.trialEndsAt ? new Date(subscription.trialEndsAt) : null)

    // If we have an end date, IT is the source of truth — not the
    // Stripe status field. Stripe transitions `trialing` → `canceled`
    // via a webhook that can lag (or never fire, e.g. for
    // cancelAtPeriodEnd on a Free Trial with priceId=null). Previously
    // an expired end date fell through to the status check below and
    // access silently continued for hours/days past the true expiry —
    // that's what let Phil browse the portal after his Free Trial
    // ended on Sept 3.
    if (endDate) {
      return endDate > new Date()
    }

    // No end date on the doc — fall back to the status field.
    if (status === 'active' || status === 'trialing') return true

    return false
  }


const value = {
    currentUser,
    userProfile,
    loading,
    tokenAuthInProgress,
    socialSignUpInProgress,
    error,
    subscription,
    subscriptionLoading,
    isSubscriptionActive,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signUp,
    signUpWithGoogle,
    signUpWithApple,
    signOut,
    logout: signOut,
    resetPassword,
    updateEmail,
    sendVerificationEmail,
    resendEmailChangeVerification,
    changePassword,
    cancelPendingEmailChange,
    signOutAllDevices,
    updateProfile,
    loadUserProfile,
    updateUserNeighborhood,
    isNeighborHidden,
    hideNeighbor,
    unhideNeighbor
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
